import { Link, router } from '@inertiajs/react'
import { formatLempiras } from '../../lib/format'
import { useState } from 'react'
import Layout from '../Layout'

interface Account { id: number; name: string; slug: string }
interface Order { id: number; sequential_number: number; state: string; customer_name: string; customer_email: string; total_cents: number; fulfillment_type: string; branch_name?: string; placed_at: string; items_count: number }
interface Props {
  account: Account
  orders: Order[]
  states: string[]
  current_state?: string
  search?: string
  from?: string | null
  to?: string | null
  show_all?: boolean
}

// Fecha local YYYY-MM-DD (ajustada por offset UTC para no cruzar de día)
function todayStr(): string {
  const d = new Date()
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10)
}

const STATE_LABELS: Record<string, { label: string; color: string }> = {
  pending: { label: 'Pendiente', color: 'bg-gray-100 text-gray-700' },
  awaiting_payment: { label: 'Esperando pago', color: 'bg-yellow-100 text-yellow-700' },
  confirming: { label: 'Pago en verificación', color: 'bg-orange-100 text-orange-700' },
  paid: { label: 'Pagado', color: 'bg-blue-100 text-blue-700' },
  ready_for_pickup: { label: 'Listo p/recoger', color: 'bg-green-100 text-green-700' },
  shipped: { label: 'Enviado', color: 'bg-green-100 text-green-700' },
  completed: { label: 'Completado', color: 'bg-green-200 text-green-800' },
  cancelled: { label: 'Cancelado', color: 'bg-red-100 text-red-700' },
}

export default function OrdersIndex({ account, orders, states, current_state, search, from: fromProp, to: toProp, show_all: showAllProp }: Props) {
  const [s, setS] = useState(search || '')
  const [from, setFrom] = useState<string>(fromProp || toProp || todayStr())
  const [to, setTo] = useState<string>(toProp || fromProp || todayStr())
  const [showAll, setShowAll] = useState<boolean>(!!showAllProp)

  // Query string con TODOS los filtros activos (fechas + estado + búsqueda)
  const qs = (extra: Record<string, string | undefined> = {}): string => {
    const p = new URLSearchParams()
    if (showAll) p.set('all', '1')
    else {
      if (from) p.set('from', from)
      if (to) p.set('to', to)
    }
    if (current_state) p.set('state', current_state)
    if (s) p.set('search', s)
    for (const [k, v] of Object.entries(extra)) {
      if (v) p.set(k, v)
      else p.delete(k)
    }
    const q = p.toString()
    return q ? `?${q}` : ''
  }

  const applyFilter = (fromVal: string, toVal: string, all: boolean) => {
    setShowAll(all)
    router.get(
      `/${account.slug}/orders`,
      all ? { all: '1', state: current_state, search: s } : { from: fromVal, to: toVal, state: current_state, search: s },
      { preserveState: true, replace: true }
    )
  }

  const onFromChange = (v: string) => {
    setFrom(v)
    const effectiveTo = to < v ? v : to
    setTo(effectiveTo)
    if (v) applyFilter(v, effectiveTo, false)
  }

  const onToChange = (v: string) => {
    setTo(v)
    const effectiveFrom = v && from > v ? v : from
    setFrom(effectiveFrom)
    if (v) applyFilter(effectiveFrom, v, false)
  }

  const handleSearch = (v: string) => {
    setS(v)
    router.get(`/${account.slug}/orders${qs({ search: v })}`, {}, { preserveState: true, replace: true })
  }

  return (
    <Layout account={account}>
    <div className="max-w-7xl mx-auto px-4 py-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-[#202020]">Pedidos Web</h1>
        <p className="text-sm text-gray-500">{orders.length} pedidos</p>
      </div>

      <div className="bg-white rounded-lg shadow p-4 mb-4 space-y-3">
        <input type="text" placeholder="Buscar por nombre, email o # pedido..." value={s}
          onChange={(e) => handleSearch(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded" />
        <div className="flex flex-wrap gap-2">
          <Link href={`/${account.slug}/orders${qs({ state: undefined })}`}
            className={`px-3 py-1.5 rounded text-sm ${!current_state ? 'bg-[#55ACEE] text-white' : 'bg-gray-100 text-gray-700'}`}>
            Todos
          </Link>
          {states.map((st) => (
            <Link key={st} href={`/${account.slug}/orders${qs({ state: st })}`}
              className={`px-3 py-1.5 rounded text-sm ${current_state === st ? 'bg-[#55ACEE] text-white' : 'bg-gray-100 text-gray-700'}`}>
              {STATE_LABELS[st]?.label || st}
            </Link>
          ))}
        </div>

        {/* Filtro de fechas: por defecto solo hoy; selector para ver días anteriores */}
        <div className="flex flex-wrap items-end gap-3 border-t pt-3">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Desde</label>
            <input
              type="date"
              value={from}
              onChange={(e) => onFromChange(e.target.value)}
              className="border border-gray-300 rounded px-2 py-1.5 text-sm text-[#202020]"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Hasta</label>
            <input
              type="date"
              value={to}
              onChange={(e) => onToChange(e.target.value)}
              className="border border-gray-300 rounded px-2 py-1.5 text-sm text-[#202020]"
            />
          </div>
          <button
            onClick={() => {
              const t = todayStr()
              setFrom(t)
              setTo(t)
              applyFilter(t, t, false)
            }}
            className={`px-3 py-1.5 rounded text-sm font-medium border transition-colors ${
              !showAll && from === to ? 'bg-[#55ACEE] text-white border-[#55ACEE]' : 'bg-white text-[#202020] border-gray-300 hover:bg-gray-100'
            }`}
          >
            Hoy
          </button>
          <button
            onClick={() => applyFilter('', '', true)}
            className={`px-3 py-1.5 rounded text-sm font-medium border transition-colors ${
              showAll ? 'bg-[#55ACEE] text-white border-[#55ACEE]' : 'bg-white text-[#202020] border-gray-300 hover:bg-gray-100'
            }`}
          >
            Todo
          </button>
        </div>
      </div>

      {orders.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <p className="text-gray-500">No hay pedidos</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">#</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cliente</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Estado</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Entrega</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Items</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fecha</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {orders.map((o) => {
                const s = STATE_LABELS[o.state] || STATE_LABELS.pending
                return (
                  <tr key={o.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <Link href={`/${account.slug}/orders/${o.id}`} className="text-[#55ACEE] hover:underline font-mono">#{o.sequential_number}</Link>
                    </td>
                    <td className="px-4 py-3">
                      <p className="font-medium text-[#202020]">{o.customer_name}</p>
                      <p className="text-xs text-gray-500">{o.customer_email}</p>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-xs font-semibold px-2 py-1 rounded ${s.color}`}>{s.label}</span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {o.fulfillment_type === 'pickup' ? `🏪 ${o.branch_name || '-'}` : '🚚 Envío'}
                    </td>
                    <td className="px-4 py-3 text-sm">{o.items_count}</td>
                    <td className="px-4 py-3 text-right font-semibold">{formatLempiras(o.total_cents)}</td>
                    <td className="px-4 py-3 text-xs text-gray-500">{new Date(o.placed_at).toLocaleDateString('es-HN')}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
    </Layout>
  )
}
